/**
 * Gedeelde formatting-helpers voor de Gap-diff-weergaven (tabel + model), zodat
 * beide attribuut-namen en -waarden identiek tonen. Puur: alleen registry +
 * bilingual-resolve, geen React.
 */
import { getRegistry, resolveBilingual } from "../../registry";

/** True als `v` een bilingual {nl?,en?}-bag is (alleen nl/en als keys). */
export function isBilingual(v: unknown): v is { nl?: string; en?: string } {
  if (typeof v !== "object" || v === null || Array.isArray(v)) return false;
  const keys = Object.keys(v);
  return keys.length > 0 && keys.every((k) => k === "nl" || k === "en");
}

/**
 * Resolve élke geneste bilingual {nl,en}-bag naar de UI-taal (bv.
 * `attributes.risk_label = {nl,en}`), zodat overal de waarde in de canvas-taal
 * verschijnt i.p.v. rauwe meertalige JSON.
 */
export function deepResolveLang(v: unknown, lang: string): unknown {
  if (isBilingual(v)) return resolveBilingual(v, lang);
  if (Array.isArray(v)) return v.map((x) => deepResolveLang(x, lang));
  if (typeof v === "object" && v !== null) {
    const out: Record<string, unknown> = {};
    for (const [k, val] of Object.entries(v)) out[k] = deepResolveLang(val, lang);
    return out;
  }
  return v;
}

/** NTB/TBD = universele "nog niet bepaald"-sentinel; GEEN echte waarde. */
export function isTbdPlaceholder(v: unknown): boolean {
  const hit = (s: unknown) => typeof s === "string" && (s.trim() === "NTB" || s.trim() === "TBD");
  if (hit(v)) return true;
  if (isBilingual(v)) return hit(v.nl) || hit(v.en);
  return false;
}

/**
 * True als `v` "leeg" is: null/undefined, lege string/array/object, of een
 * bilingual bag waarvan alle talen leeg zijn. Booleans (ook `false`) niet.
 */
export function isEmptyValue(v: unknown): boolean {
  if (v == null) return true;
  if (typeof v === "string") return v.trim() === "";
  if (Array.isArray(v)) return v.length === 0;
  if (typeof v === "object") {
    const vals = Object.values(v);
    if (vals.length === 0) return true;
    return vals.every((x) => x == null || (typeof x === "string" && x.trim() === ""));
  }
  return false;
}

/** Stereotype-id-array → komma-gescheiden labels (per UI-taal). */
export function stereoLabels(v: unknown, lang: string): string {
  if (v == null) return "—";
  if (!Array.isArray(v)) return String(v);
  if (v.length === 0) return "—";
  const reg = getRegistry();
  return v
    .map((sid) => {
      const st = reg.getStereotype(String(sid));
      return st ? resolveBilingual(st.label, lang) : String(sid);
    })
    .join(", ");
}

/**
 * Type-label voor een object/relatie: stereotype-label als 't een stereotype
 * draagt (analoog aan de Explorer-groepering), anders het element-/relatie-type-
 * label, met de rauwe typeId als laatste fallback.
 */
export function conceptTypeLabel(
  typeId: string,
  stereotypes: string[] | undefined,
  lang: string,
): string {
  const reg = getRegistry();
  for (const sid of stereotypes ?? []) {
    const st = reg.getStereotype(sid);
    if (st) return resolveBilingual(st.label, lang);
  }
  const et = reg.getElementType(typeId);
  if (et) return resolveBilingual(et.label, lang);
  const rt = reg.getRelationType(typeId);
  if (rt) return resolveBilingual(rt.label, lang);
  return typeId;
}

export interface AttrSchema {
  option_labels?: Record<string, { nl?: string; en?: string }>;
  /** Attribuut-type (bv. `"reference?"`, `"enum"`) — voor reference-resolutie. */
  type?: string;
}

/**
 * Resolveert een attribuut-sleutel naar z'n leesbare veldnaam + schema.
 * Hoort 't bij een stereotype van het concept → `<stereotype>.<attr>`; bij het
 * element-/relatie-type → het attribuut-label; anders de kale sleutel. Het
 * schema geven we terug zodat de waarde enum-option-labels kan gebruiken.
 */
export function attrInfo(
  key: string,
  stereotypes: string[] | undefined,
  typeId: string | undefined,
  lang: string,
): { field: string; schema: AttrSchema | null } {
  const reg = getRegistry();
  for (const sid of stereotypes ?? []) {
    const st = reg.getStereotype(sid);
    if (!st) continue;
    const a = st.attributes?.find((x) => x.name === key);
    if (a) {
      return {
        field: `${resolveBilingual(st.label, lang)}.${resolveBilingual(a.label, lang)}`,
        schema: a,
      };
    }
  }
  if (typeId) {
    const a =
      reg.getElementType(typeId)?.attributes?.find((x) => x.name === key) ??
      reg.getRelationType(typeId)?.attributes?.find((x) => x.name === key);
    if (a) return { field: resolveBilingual(a.label, lang), schema: a };
  }
  return { field: key, schema: null };
}

/**
 * Formatteer één attribuut-waarde zoals de tabel-weergave: enum-option-label →
 * bilingual-resolve → boolean → JSON-fallback. NTB/TBD en lege waarden → "—".
 */
export function formatAttrValue(
  value: unknown,
  schema: AttrSchema | null,
  lang: string,
  resolveRef?: (id: string) => string | undefined,
): string {
  if (isEmptyValue(value) || isTbdPlaceholder(value)) return "—";
  // Reference-attribuut (bv. `attribute_type_ref`): toon de naam van het
  // gerefereerde concept i.p.v. de rauwe UUID.
  if (typeof value === "string" && schema?.type?.startsWith("reference") && resolveRef) {
    return resolveRef(value) ?? value;
  }
  if (typeof value === "string" && schema?.option_labels?.[value]) {
    return resolveBilingual(schema.option_labels[value], lang) || value;
  }
  const resolved = deepResolveLang(value, lang);
  if (typeof resolved === "string") return resolved;
  if (typeof resolved === "boolean") return resolved ? "✓" : "✗";
  if (resolved == null) return "—";
  return JSON.stringify(resolved);
}
